import { useMemo } from 'react';

import { LineChart } from '@/components/charts/line-chart';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/lib/utils';

import type { PriceHistoryPoint } from '../dashboard.api';

interface PriceHistoryChartProps {
  data: PriceHistoryPoint[] | undefined;
  loading: boolean;
  height?: number;
}

interface SeriePrecio {
  combustible: string;
  color: string;
  precio: number;
  minimo: number;
  maximo: number;
  variacion: number;
}

/**
 * Variación del precio promedio diario por combustible, con el último precio y
 * su variación respecto al primer día del rango.
 */
export function PriceHistoryChart({ data, loading, height }: PriceHistoryChartProps) {
  const { labels, series, resumen } = useMemo(() => {
    const puntos = (data ?? []).filter((punto) => punto && punto.fecha && punto.combustible);

    const fechas = [...new Set(puntos.map((punto) => String(punto.fecha).substring(0, 10)))].sort();

    const indice = new Map<string, number>();
    for (const punto of puntos) {
      const precio = Number(punto.precio_promedio || 0);
      if (precio > 0) {
        indice.set(`${punto.combustible}|${String(punto.fecha).substring(0, 10)}`, precio);
      }
    }

    // Sólo los productos que tuvieron precio en el rango: un producto sin ventas
    // (precio 0) deformaría la escala de la gráfica.
    const combustibles = [...new Set(puntos.map((punto) => punto.combustible))].filter((combustible) =>
      fechas.some((fecha) => indice.has(`${combustible}|${fecha}`)),
    );

    const series = combustibles.map((combustible) => ({
      label: combustible,
      // El color de la base (producto) se usa en la línea y en las tarjetas de
      // abajo, para que el usuario relacione ambas de un vistazo.
      color: puntos.find((punto) => punto.combustible === combustible)?.color || undefined,
      data: fechas.map((fecha) => indice.get(`${combustible}|${fecha}`) ?? null),
    }));

    const resumen: SeriePrecio[] = combustibles.map((combustible) => {
      const valores = fechas
        .map((fecha) => indice.get(`${combustible}|${fecha}`))
        .filter((valor): valor is number => typeof valor === 'number');
      const origen = valores[0] ?? 0;
      const precio = valores[valores.length - 1] ?? 0;

      const muestra = puntos.find((punto) => punto.combustible === combustible);

      return {
        combustible,
        color: muestra?.color || '#0ea5e9',
        precio,
        minimo: Math.min(...valores),
        maximo: Math.max(...valores),
        variacion: origen > 0 ? ((precio - origen) / origen) * 100 : 0,
      };
    });

    return { labels: fechas, series, resumen };
  }, [data]);

  if (loading) {
    return <Skeleton className="h-72 rounded-control" />;
  }

  if (labels.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-500">
        Sin precios registrados en este rango (PTS no reportó transacciones).
      </p>
    );
  }

  return (
    <div className="flex h-full flex-col justify-center gap-4">
      <LineChart
        labels={labels}
        series={series}
        format="currency"
        spanGaps
        showLegend={false}
        height={height}
      />

      <ul className="grid gap-2 border-t border-ink-100 pt-3 sm:grid-cols-2 xl:grid-cols-3">
        {resumen.map((item) => (
          <li key={item.combustible} className="rounded-control border border-ink-100 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <span
                  aria-hidden="true"
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="truncate text-xs font-medium text-ink-700">{item.combustible}</span>
              </span>

              {item.variacion === 0 ? (
                <Badge variant="neutral">sin variación</Badge>
              ) : (
                <Badge variant={item.variacion > 0 ? 'danger' : 'success'}>
                  {item.variacion > 0 ? '+' : ''}
                  {item.variacion.toFixed(2)}%
                </Badge>
              )}
            </div>

            <p className="mt-1 font-mono text-sm font-semibold text-ink-900 tabular-nums">
              {formatCurrency(item.precio)}
              <span className="ml-1 text-[11px] font-normal text-ink-400">/ gal</span>
            </p>

            <p className="text-[11px] text-ink-400">
              Mín {formatCurrency(item.minimo)} · Máx {formatCurrency(item.maximo)}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
